/* import __COLOCATED_TEMPLATE__ from './priority-level-filter.hbs'; */
/* RESPONSIBLE TEAM: team-inbox-experience */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import type IntlService from 'embercom/services/intl';

export type PriorityComparison = 'eq' | 'gt' | 'lt';

interface Option {
  value: number;
  label: string;
}

interface DropdownItem {
  text: string;
  id: string;
}

interface Signature {
  Args: {
    selected?: number | string;
    options: Option[];
    onSelect: (value: number) => void;
    comparisonSelected?: PriorityComparison;
    onComparisonSelect?: (comparison: PriorityComparison) => void;
  };
}

export default class PriorityLevelFilter extends Component<Signature> {
  @service declare intl: IntlService;

  get comparisonOptions() {
    return [
      {
        value: 'eq' as PriorityComparison,
        label: this.intl.t('inbox.search.filters.more-filters.is'),
      },
      {
        value: 'gt' as PriorityComparison,
        label: this.intl.t('inbox.search.filters.more-filters.more-than'),
      },
      {
        value: 'lt' as PriorityComparison,
        label: this.intl.t('inbox.search.filters.more-filters.less-than'),
      },
    ];
  }

  get groupList() {
    return [
      {
        items: this.args.options.map((option) => ({
          text: option.label,
          id: String(option.value),
        })),
      },
    ];
  }

  get selectedLabel(): string {
    let { selected } = this.args;
    // Treat undefined, null, and non-numeric strings (e.g. the 'none' sentinel
    // passed when no level is selected yet) the same as "nothing chosen" —
    // fall back to the first option's label so the dropdown is never blank.
    if (typeof selected === 'number') {
      return this.args.options.find((o) => o.value === selected)?.label ?? '';
    }
    let numericSelected = selected != null ? Number(selected) : NaN;
    if (isNaN(numericSelected)) {
      return this.args.options[0]?.label ?? '';
    }
    return this.args.options.find((o) => o.value === numericSelected)?.label ?? '';
  }

  @action onSelectItem(item: DropdownItem) {
    this.args.onSelect(Number(item.id));
  }

  @action onComparisonSelect(comparison: PriorityComparison) {
    this.args.onComparisonSelect?.(comparison);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::Search::PriorityLevelFilter': typeof PriorityLevelFilter;
    'inbox2/search/priority-level-filter': typeof PriorityLevelFilter;
  }
}
